import {
  DryingProjectOverview,
  calculateDryingProgressPercent,
} from "./DryingProjectOverview";
import LeakDetectionProjectOverview from "./LeakDetectionProjectOverview";
import { ProjectConclusionState } from "./ProjectDetails";
import { getProjectConclusionState, isProjectDeleted } from "./ProjectOverview";

export enum ProjectFilter {
  ALL_PROJECTS,
  PROJECTS_WITH_ONGOING_DRYING,
  PROJECTS_WITH_DEVICES_OR_FINISHED_PROJECTS,
  OVERDUE_PROJECTS_OR_PROJECTS_MOVED_TO_FINISHED,
  RECENTLY_FINISHED_PROJECTS_OR_RESTORED_PROJECTS,
}

export enum ArchivedProjectFilter {
  ARCHIVED_PROJECTS,
  FINISHED_PROJECTS,
  PROJECTS_MOVED_TO_FINISHED,
  DELETED_PROJECTS,
}

export function doesProjectMatchFilter(
  filter: ProjectFilter,
  project: DryingProjectOverview | LeakDetectionProjectOverview,
  recentlyFinishedProjectsThreshold: number,
  isShowingFinishedProjects?: boolean
) {
  const cutOffDate = new Date();
  cutOffDate.setDate(cutOffDate.getDate() - recentlyFinishedProjectsThreshold);

  const dryingProjectOverview = project as DryingProjectOverview;
  const projectConclusionState = getProjectConclusionState(project);

  switch (filter) {
    case ProjectFilter.ALL_PROJECTS:
      return true;
    case ProjectFilter.PROJECTS_WITH_ONGOING_DRYING: {
      const dryingProjectSteps = dryingProjectOverview.dryingProjectSteps;

      return (
        dryingProjectSteps &&
        dryingProjectSteps.dryingSetupCompletions &&
        !dryingProjectSteps.dryingConclusionResult &&
        (
          !dryingProjectSteps.dryingConclusionResults ||
          dryingProjectSteps.dryingConclusionResults.length === 0
        )
      );
    }
    case ProjectFilter.PROJECTS_WITH_DEVICES_OR_FINISHED_PROJECTS:
      if (isShowingFinishedProjects) {
        return (
          project.endDate &&
          (!projectConclusionState ||
            projectConclusionState === ProjectConclusionState.FINISHED)
        );
      } else {
        return (
          dryingProjectOverview &&
          dryingProjectOverview.assignedPanels &&
          dryingProjectOverview.assignedPanels.length > 0
        );
      }

    case ProjectFilter.OVERDUE_PROJECTS_OR_PROJECTS_MOVED_TO_FINISHED:
      if (isShowingFinishedProjects) {
        return (
          projectConclusionState === ProjectConclusionState.MOVED_TO_FINSIHED
        );
      } else {
        return (
          dryingProjectOverview &&
          calculateDryingProgressPercent(dryingProjectOverview) > 1
        );
      }

    case ProjectFilter.RECENTLY_FINISHED_PROJECTS_OR_RESTORED_PROJECTS:
      if (isShowingFinishedProjects) {
        return projectConclusionState === ProjectConclusionState.RESTORED;
      } else {
        return (
          project.endDate &&
          project.endDate.toDate().getTime() > cutOffDate.getTime() &&
          (!projectConclusionState ||
            projectConclusionState === ProjectConclusionState.FINISHED)
        );
      }
  }
}

export function doesArchivedProjectMatchFilter(
  filter: ArchivedProjectFilter,
  project: DryingProjectOverview | LeakDetectionProjectOverview
) {
  const dryingProjectOverview = project as DryingProjectOverview;
  const projectConclusionState = getProjectConclusionState(project);

  switch (filter) {
    case ArchivedProjectFilter.ARCHIVED_PROJECTS:
      return (
        dryingProjectOverview &&
        !isProjectDeleted(dryingProjectOverview)
      );
    case ArchivedProjectFilter.DELETED_PROJECTS:
      return (
        dryingProjectOverview &&
        isProjectDeleted(dryingProjectOverview)
      );
    case ArchivedProjectFilter.FINISHED_PROJECTS:
      return (
        dryingProjectOverview &&
        !isProjectDeleted(dryingProjectOverview) &&
        (
          !projectConclusionState ||
          projectConclusionState === ProjectConclusionState.FINISHED
        )
      );
    case ArchivedProjectFilter.PROJECTS_MOVED_TO_FINISHED:
      return (
        dryingProjectOverview &&
        !isProjectDeleted(dryingProjectOverview) &&
        projectConclusionState === ProjectConclusionState.MOVED_TO_FINSIHED
      );
  }
}

export function getFilterName(
  vue: Vue, 
  projectFilter: ProjectFilter,
  isShowingFinishedProjects?: boolean,
) {
  switch (projectFilter) {
    case ProjectFilter.ALL_PROJECTS:
      return vue.$t("projectList.filterAllProjects").toString();

    case ProjectFilter.PROJECTS_WITH_ONGOING_DRYING:
      return vue.$t("projectList.filterProjectsWithOngoingDrying").toString();
      
    case ProjectFilter.PROJECTS_WITH_DEVICES_OR_FINISHED_PROJECTS:
      if (isShowingFinishedProjects) {
        return vue.$t("projectList.filterRecentlyFinished").toString(); 
      }
      else {
        return vue.$t("projectList.filterWithDevices").toString();
      }

    case ProjectFilter.OVERDUE_PROJECTS_OR_PROJECTS_MOVED_TO_FINISHED:
      if (isShowingFinishedProjects) {
        return vue.$t("projectList.filterProjectsMovedToFinished").toString(); 
      }
      else {
        return vue.$t("projectList.filterOverdue").toString();
      }

    case ProjectFilter.RECENTLY_FINISHED_PROJECTS_OR_RESTORED_PROJECTS:
      if (isShowingFinishedProjects) {
        return vue.$t("projectList.filterRestoredProjects").toString(); 
      }
      else {
        return vue.$t("projectList.filterRecentlyFinished").toString();
      }
  }
}

export function getArchivedFilterName(vue: Vue, archivedProjectFilter: ArchivedProjectFilter) {
  switch (archivedProjectFilter) {
    case ArchivedProjectFilter.ARCHIVED_PROJECTS:
      return vue.$t("archivedProjectList.filterArchivedProjects").toString();
    case ArchivedProjectFilter.DELETED_PROJECTS:
      return vue.$t("archivedProjectList.filterDeletedProjects").toString();
    case ArchivedProjectFilter.FINISHED_PROJECTS:
      return vue.$t("projectList.filterRecentlyFinished").toString();
    case ArchivedProjectFilter.PROJECTS_MOVED_TO_FINISHED:
      return vue.$t("projectList.filterProjectsMovedToFinished").toString();
  }
}
